import React from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Breadcrumbs,
    Link as MuiLink,
    Divider,
    Drawer,
    FormControlLabel,
    Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import SupervisedUserCircleIcon from "@material-ui/icons/SupervisedUserCircle";
import SettingsIcon from "@material-ui/icons/Settings";
import PolicyIcon from "@material-ui/icons/Policy";
import PageviewIcon from "@material-ui/icons/Pageview";
import ImportContactsIcon from "@material-ui/icons/ImportContacts";
import SearchIcon from "@material-ui/icons/Search";
import BugReportIcon from "@material-ui/icons/BugReport";
import MapIcon from "@material-ui/icons/Map";
import TableChartIcon from "@material-ui/icons/TableChart";
import DescriptionIcon from "@material-ui/icons/Description";
import LabelIcon from "@material-ui/icons/Label";

import { connect } from "react-redux";

import Header from "../../common/Header";

const useStyles = makeStyles((theme) => ({
    listPaper: { background: "#fff", borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)" },
    nested: { paddingLeft: theme.spacing(4) },
    nested2: { paddingLeft: theme.spacing(8) },
    gridItem: {
        width: "25%",
        [theme.breakpoints.down("md")]: { width: "50%" },
        [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    card: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
    },
    chips: {
        marginTop: theme.spacing(1),
        display: "flex",
        flexWrap: "wrap",
        gap: 6

    },
    cardChips: {
        color: "#fff",
        background: "darkblue",
        marginTop: theme.spacing(1),
    },
    breadcrumbLink: {
        cursor: "pointer"
    },
    video: {
        width: "100%",
        height: 0,
        paddingBottom: "56.25%",
        background: "#e9ecef",
        borderRadius: 8,
        position: "relative",
        marginTop: theme.spacing(1),
    },
    videoLabel: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        color: "#6c757d",
    },
    iconInline: {
        verticalAlign: "middle",
        marginRight: 6,
        opacity: 0.85
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '1.15rem',
        fontWeight: 'bold',
        width: '100%',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
    cardButton: {
        color: "#1A2A80",
        border: "1px solid #1A2A80",
        backgroundColor: "transparent",
        '&:hover': {
            color: "#fff",
            backgroundColor: "#1A2A80",
        },
        variant: "outlined",
    }
}));

// Node map to icon 
const iconFor = (node) => {
    const t = (node.node_type || "").toLowerCase();
    if (node.slug === "report-issues") return <BugReportIcon />;
    if (node.slug === "site-guide") return <PageviewIcon />;
    if (node.slug === "master-contacts") return <ImportContactsIcon />;
    if (node.slug === "mobile-inspections") return <LibraryBooksIcon />;
    if (node.slug === "searches-reports") return <SearchIcon />;
    if (node.slug === "form-fields") return <DescriptionIcon />;
    if (node.slug === "map") return <MapIcon />;
    if (node.slug === "tables") return <TableChartIcon />;
    if (t === "top") return <LibraryBooksIcon />;
    if (t === "second") return <SettingsIcon />;
    if (t === "third") return <LabelIcon />;
    return <LibraryBooksIcon />;
};


const TREE_ENDPOINT = "/api/help/tree";
const TOPICS_BY_TAGS_ENDPOINT = "/api/help/topics/by-tags";

function useFetchJSON(url) {
    const [state, setState] = React.useState({ data: null, loading: !!url, error: null });

    React.useEffect(() => {
        if (!url) {
            setState({ data: null, loading: false, error: null });
            return;
        }
        let cancelled = false;
        setState({ data: null, loading: true, error: null });

        fetch(url, { credentials: "same-origin" })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((json) => {
                if (!cancelled) setState({ data: json, loading: false, error: null });
            })
            .catch((err) => {
                if (!cancelled) setState({ data: null, loading: false, error: err });
            });

        return () => { cancelled = true; };
    }, [url]);

    return state;
}

export function HelpCenter({ canViewDrafts }) {
    const classes = useStyles();

    const [openTop, setOpenTop] = React.useState({});
    const [openSecond, setOpenSecond] = React.useState({});
    const [openThird, setOpenThird] = React.useState({});
    const [crumbs, setCrumbs] = React.useState(["Help Center"]);
    const [openTopic, setOpenTopic] = React.useState(null);

    const [showDrafts, setShowDrafts] = React.useState(false);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedChips, setSelectedChips] = React.useState([]);
    const [visibleTopics, setVisibleTopics] = React.useState([]);

    const catalogUrl = `${TREE_ENDPOINT}?include_drafts=${showDrafts ? "1" : "0"}`;
    const { data: catalog, loading, error } = useFetchJSON(catalogUrl);

    React.useEffect(() => {
        setOpenTop({});
        setOpenSecond({});
        setOpenThird({});
        setCrumbs(["Help Center"]);
    }, [showDrafts]);

    const allTopics = React.useMemo(() => {
        if (!catalog) return [];

        const out = [];

        const walk = (node, path = []) => {
            if (node.topics) node.topics.forEach((t) => out.push({ ...t, path }));
            (node.children || []).forEach((c) => walk(c, [...path, c.title]));
        };

        catalog.forEach((top) => walk(top, [top.title]));

        return out;
    }, [catalog]);


    const searchableTags = React.useMemo(() => {
        const set = new Set();

        allTopics.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));

        return Array.from(set).sort();

    }, [allTopics]);


    const fetchTopicsByTags = React.useCallback((tags) => {
        if (!tags.length) { setVisibleTopics([]); return; }

        const url = `${TOPICS_BY_TAGS_ENDPOINT}?tags=${encodeURIComponent(tags.join(","))}&include_drafts=${showDrafts ? "1" : "0"}`;

        fetch(url, { credentials: "same-origin" })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((json) => {
                setVisibleTopics(Array.isArray(json.results) ? json.results : []);
            })
            .catch((err) => console.error(err));
    }, [showDrafts]);


    React.useEffect(() => {
        fetchTopicsByTags(selectedChips);
    }, [selectedChips, fetchTopicsByTags]);


    const toggleDrawer = (open) => (event) => {
        if (event?.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
        setDrawerOpen(open);
    };
    const handleChipClick = (tag) => {
        setSelectedChips((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    };
    const resetTo = (index) => {
        if (index === 0) {
            setOpenTop({}); setOpenSecond({}); setOpenThird({}); setCrumbs(["Help Center"]);
        } else if (index === 1) {
            setOpenSecond({}); setOpenThird({}); setCrumbs((c) => c.slice(0, 2));
        } else if (index === 2) {
            setOpenThird({}); setCrumbs((c) => c.slice(0, 3));
        }
    };
    const toggleTop = (node) => {
        const key = node.slug || node.id || node.title;
        setOpenTop({ [key]: !openTop[key] });
        setOpenSecond({});
        setOpenThird({});
        setCrumbs(["Help Center", node.title]);
    };
    const toggleSecond = (topNode, node) => {
        const tk = topNode.slug || topNode.id || topNode.title;
        const nk = node.slug || node.id || node.title;
        setOpenSecond({ [tk]: { [nk]: !(openSecond[tk]?.[nk]) } });
        setOpenThird({});
        setCrumbs(["Help Center", topNode.title, node.title]);
    };
    const toggleThird = (secondNode, node) => {
        const sk = secondNode.slug || secondNode.id || secondNode.title;
        const nk = node.slug || node.id || node.title;
        setOpenThird({ [sk]: { [nk]: !(openThird[sk]?.[nk]) } });
        setCrumbs((prev) => ["Help Center", ...prev.slice(1, 3), node.title]);
    };

    const renderChips = () =>
        searchableTags.map((tag) => (
            <Chip
                key={tag}
                icon={<LabelIcon className={classes.iconInline} />}
                label={`#${tag}`}
                clickable
                color={selectedChips.includes(tag) ? "primary" : "default"}
                onClick={() => handleChipClick(tag)}
                style={{ margin: "12px 8px", padding: "8px 12px" }}
            />
        ));

    return (
        <>
            <Header />

            <Box p={4}>


                <br /> <br />

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                >
                    <Typography
                        variant="h3"
                    >
                        Help Center
                    </Typography>

                    {canViewDrafts && (
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={showDrafts}
                                    onChange={(e) => setShowDrafts(e.target.checked)}
                                />
                            }
                            label="Show drafts"
                        />
                    )}
                </Box>

                <Box
                    mb={3}
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="center"
                    gap={8}
                >
                    <Button
                        onClick={toggleDrawer(true)}
                        style={{
                            width: "50%",
                            padding: "12px 16px",
                            borderRadius: 8
                        }}
                    >
                        <Typography
                            style={{
                                fontWeight: "bold",
                                color: "#1A2A80",
                                fontSize: 18
                            }}
                        >
                            Search by Keywords
                        </Typography>
                    </Button>

                    <Drawer
                        anchor="top"
                        open={drawerOpen}
                        onClose={toggleDrawer(false)}
                        keepMounted
                    >
                        <Box
                            p={2}
                            role="presentation"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 18
                                    }}
                                >
                                    Select keywords to filter help topics
                                </Typography>
                                <Box>

                                    <Button
                                        onClick={() => setSelectedChips([])}
                                        style={{
                                            marginRight: 8,
                                            width: 100
                                        }}
                                    >
                                        Clear
                                    </Button>

                                    <Button
                                        color="primary"
                                        onClick={toggleDrawer(false)}
                                        style={{
                                            marginLeft: 8,
                                            width: 100
                                        }}
                                    >
                                        Done
                                    </Button>

                                </Box>
                            </Box>
                            <Divider />

                            <Box
                                mt={1}
                                display="flex"
                                flexWrap="wrap"
                            >
                                {renderChips()}
                            </Box>

                        </Box>
                    </Drawer>
                </Box>

                {selectedChips.length > 0 && (
                    <>
                        <Box
                            mt={2}
                            width="100%"
                            textAlign="center"
                        >
                            <Typography
                                variant="h6"
                            >
                                Displaying selected tags:
                            </Typography>

                            <Box
                                mt={1}
                                display="flex"
                                justifyContent="center"
                                flexWrap="wrap"
                                gap={8}
                            >

                                {selectedChips.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        onDelete={() => handleChipClick(tag)}
                                        color="primary"
                                        style={{
                                            margin: "12px 8px",
                                            padding: "8px 12px"
                                        }}
                                    />
                                ))}

                            </Box>
                        </Box>

                        <Box
                            mt={4}
                        >
                            <Grid
                                container
                                spacing={2}
                                wrap="wrap"
                                justifyContent="space-evenly"
                            >
                                {visibleTopics.map((t) => (
                                    <Grid
                                        item
                                        key={t.id || t.title}
                                        className={classes.gridItem}
                                    >
                                        <Card
                                            className={classes.card}
                                            style={{
                                                borderRadius: 8,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                boxShadow: "none",
                                                background: "whitesmoke"
                                            }}
                                        >
                                            <CardContent>
                                                <Typography
                                                    variant="subtitle1"
                                                    className={classes.cardHeader}
                                                >
                                                    {t.title}
                                                </Typography>
                                                <hr style={{ width: "100%", borderColor: "rgba(0,0,0,0.1)" }} />
                                                <div
                                                    className={classes.chips}
                                                >
                                                    {(t.tags || []).map((tag) => (
                                                        <Chip
                                                            key={tag}
                                                            size="small"
                                                            label={`#${tag}`}
                                                            className={classes.cardChips}
                                                        />
                                                    ))}
                                                </div>
                                            </CardContent>

                                            <CardActions>
                                                <Button
                                                    className={classes.cardButton}
                                                    onClick={() => setOpenTopic(t)}
                                                >
                                                    View Video/Documentation
                                                </Button>
                                            </CardActions>

                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </>
                )}

                <br />
                <br />

                <Breadcrumbs
                    aria-label="breadcrumb"
                    style={{
                        marginBottom: 16,
                        padding: 8,
                        background: "#f8f9fa",
                        borderRadius: 8
                    }}
                >

                    {crumbs.map((c, i) => (
                        <MuiLink
                            key={i}
                            color={i === crumbs.length - 1 ? "textPrimary" : "inherit"}
                            className={classes.breadcrumbLink}
                            onClick={() => resetTo(i)}
                        >
                            {c}
                        </MuiLink>
                    ))}

                </Breadcrumbs>

                {loading && <Typography>Loading…</Typography>}
                {error && <Typography color="error">Couldn't load help catalog. Double-check your Django URL /api/help/tree.</Typography>}
                {!loading && !error && (
                    <List
                        className={classes.listPaper}
                    >
                        {(catalog || []).map((top) => {
                            const topKey = top.slug || top.id || top.title;
                            const topOpen = !!openTop[topKey];
                            return (
                                <React.Fragment
                                    key={topKey}
                                >
                                    <ListItem
                                        button
                                        onClick={() => toggleTop(top)}
                                        style={{
                                            background: topOpen ? "#1A2A80" : "inherit",
                                            borderRadius: topOpen ? 8 : 0
                                        }}
                                    >
                                        <ListItemIcon
                                            style={{
                                                color: topOpen ? "#fff" : "inherit"
                                            }}
                                        >
                                            {iconFor(top)}
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={top.title}
                                            primaryTypographyProps={{
                                                style: {
                                                    fontWeight: topOpen ? "bold" : "normal", color: topOpen ? "#fff" : "inherit",
                                                }
                                            }}
                                        />
                                        {topOpen
                                            ?
                                            <ExpandLess
                                                style={{ color: "#fff" }}
                                            />
                                            :
                                            <ExpandMore />
                                        }
                                    </ListItem>

                                    <Collapse
                                        in={topOpen}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <List
                                            disablePadding
                                        >
                                            {(top.children || []).map((second) => {
                                                const tk = topKey;
                                                const sk = second.slug || second.id || second.title;
                                                const secondOpen = !!(openSecond[tk]?.[sk]);

                                                return (
                                                    <React.Fragment
                                                        key={sk}
                                                    >
                                                        <ListItem
                                                            button
                                                            className={classes.nested}
                                                            onClick={() => toggleSecond(top, second)}
                                                            style={{
                                                                background: secondOpen ? "#7A85C1" : "rgba(26, 42, 128, 0.15)",
                                                                borderRadius: secondOpen ? 8 : 0,
                                                                marginTop: 2,
                                                            }}
                                                        >
                                                            <ListItemIcon
                                                                style={{
                                                                    color: secondOpen ? "#fff" : "inherit"
                                                                }}
                                                            >

                                                                {iconFor(second)}

                                                            </ListItemIcon>

                                                            <ListItemText
                                                                primary={second.title}
                                                                primaryTypographyProps={{
                                                                    style: { fontWeight: secondOpen ? "bold" : "normal", color: secondOpen ? "#fff" : "inherit" }
                                                                }}
                                                            />

                                                            {secondOpen
                                                                ?
                                                                <ExpandLess
                                                                    style={{ color: "#fff" }}
                                                                />
                                                                :
                                                                <ExpandMore />
                                                            }

                                                        </ListItem>

                                                        <Collapse
                                                            in={secondOpen}
                                                            timeout="auto"
                                                            unmountOnExit
                                                        >
                                                            <List
                                                                disablePadding
                                                            >
                                                                {(second.children || []).map((third) => {
                                                                    const skey = sk;
                                                                    const tk3 = third.slug || third.id || third.title;
                                                                    const thirdOpen = !!(openThird[skey]?.[tk3]);

                                                                    return (
                                                                        <React.Fragment
                                                                            key={tk3}
                                                                        >
                                                                            <ListItem
                                                                                button
                                                                                className={classes.nested2}
                                                                                onClick={() => toggleThird(second, third)}
                                                                                style={{
                                                                                    background: thirdOpen ? "#B2B0E8" : "whitesmoke",
                                                                                    borderRadius: thirdOpen ? 8 : 0,
                                                                                    marginTop: 2,
                                                                                }}
                                                                            >
                                                                                <ListItemIcon
                                                                                    style={{
                                                                                        color: thirdOpen ? "#fff" : "inherit"
                                                                                    }}
                                                                                >
                                                                                    {iconFor(third)}
                                                                                </ListItemIcon>

                                                                                <ListItemText
                                                                                    primary={third.title}
                                                                                    primaryTypographyProps={{
                                                                                        style: {
                                                                                            fontWeight: thirdOpen ? "bold" : "normal", color: thirdOpen ? "#fff" : "inherit"
                                                                                        }
                                                                                    }}
                                                                                />

                                                                                {thirdOpen ? <ExpandLess style={{ color: "#fff" }} /> : <ExpandMore />}

                                                                            </ListItem>

                                                                            <Collapse
                                                                                in={thirdOpen}
                                                                                timeout="auto"
                                                                                unmountOnExit
                                                                            >

                                                                                <Box
                                                                                    px={4}
                                                                                    pb={2}
                                                                                    pt={2}
                                                                                >

                                                                                    <Grid
                                                                                        container
                                                                                        spacing={2}
                                                                                        wrap="wrap"
                                                                                        justifyContent="space-evenly"
                                                                                    >

                                                                                        {(third.topics || []).map((t) => (
                                                                                            <Grid
                                                                                                item
                                                                                                key={t.id || t.title}
                                                                                                className={classes.gridItem}
                                                                                            >

                                                                                                <Card
                                                                                                    className={classes.card}
                                                                                                    style={{
                                                                                                        borderRadius: 8,
                                                                                                        border: "1px solid rgba(0,0,0,0.08)",
                                                                                                        boxShadow: "none",
                                                                                                        background: "whitesmoke",
                                                                                                    }}
                                                                                                >
                                                                                                    <CardContent>

                                                                                                        <Typography
                                                                                                            variant="subtitle1"
                                                                                                            className={classes.cardHeader}
                                                                                                        >
                                                                                                            {t.title}
                                                                                                        </Typography>
                                                                                                        <hr style={{ width: "100%", borderColor: "rgba(0,0,0,0.1)" }} />
                                                                                                        <div className={classes.chips}>
                                                                                                            {(t.tags || []).map((tag) => (
                                                                                                                <Chip
                                                                                                                    key={tag}
                                                                                                                    size="small"
                                                                                                                    label={`#${tag}`}
                                                                                                                    className={classes.cardChips}
                                                                                                                />
                                                                                                            ))}
                                                                                                        </div>

                                                                                                    </CardContent>

                                                                                                    <CardActions>
                                                                                                        <Button
                                                                                                            className={classes.cardButton}
                                                                                                            onClick={() => setOpenTopic({ ...t, path: [top.title, second.title, third.title] })}
                                                                                                        >
                                                                                                            View Video/Documentation
                                                                                                        </Button>
                                                                                                    </CardActions>
                                                                                                </Card>
                                                                                            </Grid>
                                                                                        ))}
                                                                                    </Grid>
                                                                                </Box>
                                                                            </Collapse>
                                                                        </React.Fragment>
                                                                    );
                                                                })}

                                                                {(second.topics || []).length > 0 && (
                                                                    <>
                                                                        <Divider />
                                                                        <Box
                                                                            px={4}
                                                                            pb={2}
                                                                            pt={2}
                                                                        >
                                                                            <Grid
                                                                                container
                                                                                spacing={2}
                                                                                wrap="wrap"
                                                                                justifyContent="space-evenly"
                                                                            >
                                                                                {(second.topics || []).map((t) => (
                                                                                    <Grid
                                                                                        item
                                                                                        key={t.id || t.title}
                                                                                        className={classes.gridItem}
                                                                                    >
                                                                                        <Card
                                                                                            className={classes.card}
                                                                                            style={{
                                                                                                borderRadius: 8,
                                                                                                border: "1px solid rgba(0,0,0,0.08)",
                                                                                                boxShadow: "none",
                                                                                                background: "whitesmoke",
                                                                                            }}
                                                                                        >
                                                                                            <CardContent>
                                                                                                <Typography
                                                                                                    variant="subtitle1"
                                                                                                    className={classes.cardHeader}
                                                                                                >
                                                                                                    {t.title}
                                                                                                </Typography>
                                                                                                <hr style={{ width: "100%", borderColor: "rgba(0,0,0,0.1)" }} />
                                                                                                <div
                                                                                                    className={classes.chips}
                                                                                                >
                                                                                                    {(t.tags || []).map((tag) => (
                                                                                                        <Chip
                                                                                                            key={tag}
                                                                                                            size="small"
                                                                                                            label={`#${tag}`}
                                                                                                            className={classes.cardChips}
                                                                                                        />
                                                                                                    ))}
                                                                                                </div>
                                                                                            </CardContent>
                                                                                            <CardActions>
                                                                                                <Button
                                                                                                    className={classes.cardButton}
                                                                                                    onClick={() => setOpenTopic({ ...t, path: [top.title, second.title] })}
                                                                                                >
                                                                                                    View Video/Documentation
                                                                                                </Button>
                                                                                            </CardActions>
                                                                                        </Card>
                                                                                    </Grid>
                                                                                ))}
                                                                            </Grid>
                                                                        </Box>
                                                                    </>
                                                                )}
                                                            </List>
                                                        </Collapse>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>
                                    </Collapse>
                                    <Divider />
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}

                <Dialog
                    open={Boolean(openTopic)}
                    onClose={() => setOpenTopic(null)}
                    fullWidth
                    maxWidth="sm"
                >

                    <DialogTitle>
                        {openTopic?.title}
                    </DialogTitle>

                    <DialogContent
                        dividers
                    >

                        <Typography
                            variant="body2"
                            color="textSecondary"
                            gutterBottom
                        >
                            {openTopic ? ["Help Center", ...(openTopic.path || [])].join(" › ") : ""}
                        </Typography>

                        {openTopic?.video_url ? (
                            <div
                                className={classes.video}
                            >
                                <iframe
                                    title="topic-video"
                                    src={openTopic.video_url}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        border: 0
                                    }}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <div
                                className={classes.video}
                            >
                                <Typography
                                    className={classes.videoLabel}
                                >
                                    Video placeholder
                                </Typography>
                            </div>
                        )}

                        {/* Document */}
                        <Box mt={2}>
                            <Typography variant="subtitle2">Document</Typography>
                            {openTopic?.document ? (
                                <Box mt={1}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        component="a"
                                        href={openTopic.document}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Open attached document
                                    </Button>
                                </Box>
                            ) : openTopic?.body_md ? (
                                <Box mt={1}>
                                    <Typography component="pre" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                                        {openTopic.body_md}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography color="textSecondary">
                                    Placeholder document content. Will be replaced with actual help content in future.
                                </Typography>
                            )}
                        </Box>

                        {openTopic?.tags?.length ? (
                            <Box
                                mt={2}
                                className={classes.chips}
                            >
                                {openTopic.tags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        size="small"
                                        label={`#${tag}`}
                                        className={classes.cardChips}
                                    />
                                ))}
                            </Box>
                        ) : null}
                    </DialogContent>

                    <DialogActions>

                        <Button
                            onClick={() => setOpenTopic(null)}
                            color="primary"
                        >
                            Close
                        </Button>

                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}

const pickUser = (state) => {
    const candidates = [
        state.auth && state.auth.user,
        state.authState && state.authState.user,
        state.nav && state.nav.user,
        state.sync && state.sync.user,
        state.users && (state.users.current || state.users.user || state.users.me),
    ];
    return candidates.find(Boolean) || null;
};

const normTruthy = (v) => {
    if (v == null) return false;
    if (typeof v === "string") return ["true", "1", "yes", "y"].includes(v.trim().toLowerCase());
    if (typeof v === "number") return v === 1;
    return !!v;
};


const mapStateToProps = (state) => {
    const user = pickUser(state);

    const toNames = (arr) =>
        (arr || [])
            .map((x) => (typeof x === "string" ? x : x?.name || x?.codename || x?.id))
            .filter(Boolean)
            .map((s) => String(s).toLowerCase());

    const roles = new Set([...toNames(user?.roles), ...toNames(user?.groups)]);
    const perms = new Set(toNames(user?.permissions || user?.perms));

    const roleHas = (substr) => [...roles].some((r) => r.includes(substr.toLowerCase()));
    const permHas = (substr) => [...perms].some((p) => p.includes(substr.toLowerCase()));

    const isSuper = normTruthy(user?.is_superuser) || normTruthy(user?.isSuperuser);
    const isStaff = normTruthy(user?.is_staff) || normTruthy(user?.isStaff) || roleHas("staff");
    const isAdmin = normTruthy(user?.is_admin) || normTruthy(user?.isAdmin) || roleHas("admin");
    const isCityAdmin = normTruthy(user?.is_city_admin) || normTruthy(user?.isCityAdmin) || roleHas("city_admin") || roleHas("city-admin") || roleHas("cityadmin");

    const hasDraftPerm = permHas("can_view_drafts") || permHas(":can_view_drafts") || permHas("draft");

    // Setting who can see drafts to only superusers and city admins for now
    // const canViewDrafts = !!user && (isSuper || isStaff || isAdmin || isCityAdmin || hasDraftPerm);
    const canViewDrafts = !!user && (isSuper, isCityAdmin);

    return { canViewDrafts };
};


export default connect(mapStateToProps)(HelpCenter);